import { values } from "lodash";
import { Device, DeviceFamily, DeviceId, DeviceType, Theme } from 'shared/models';
import { matchesResolution } from 'shared/util/matchesResolution';

const stringComparer = new Intl.Collator(undefined, {
    sensitivity: 'base',
    ignorePunctuation: true,
    numeric: true,
});

export function getDevicesCapableOfTheme(
    devices: Record<DeviceId, Device>,
    deviceTypes: Record<string, DeviceType>,
    theme: Theme,
) {
    const {
        width = 0,
        height = 0,
        requirements = {},
    } = theme;

    // Only show devices that can support the theme
    const capableDevices = values(devices).filter(({ deviceTypeId, properties }) => {
        const deviceType = deviceTypes[deviceTypeId];
        if (!deviceType) {
            return false;
        }

        const themeDeviceFamily = requirements?.deviceTypeId ? deviceTypes[requirements.deviceTypeId].deviceFamily : null;
        
        // Dimensions of the device must match the theme (or rotated)
        // unless the device allows themes of any size
        const { pixelWidth, pixelHeight, deviceCapabilities } = deviceType;
        if (!matchesResolution(width, height, pixelWidth || 0, pixelHeight || 0) && !deviceCapabilities.anySize) {
            return false;
        }

        // The theme can only go on the associated device type
        if ((requirements.TTG && !deviceCapabilities.TTG) ||
            (requirements.Matisse && !deviceCapabilities.Matisse) ||
            (themeDeviceFamily === DeviceFamily.Giotto && deviceType.deviceFamily !== DeviceFamily.Giotto)) {
            return false;
        }

        // PI themes can only go on PI devices and standard themes can only go on standard devices
        if (!!requirements.pi !== !!properties.pi) {
            return false;
        }

        return true;
    });

    // Put no-name devices first (and then sorted by ID)
    // Put named devices last, sorted by name
    return capableDevices.sort((a, b) => {
        if (!a.displayName) {
            if (!b.displayName) {
                return stringComparer.compare(a.id, b.id);
            }
            return -1;
        }
        else if (!b.displayName) {
            return 1;
        }
        return stringComparer.compare(a.displayName, b.displayName);
    });
}
